/**
 * Portal Manta · NovoEstudoFluxoPage
 * Wizard principal de Novo Estudo (Cards 1-6 · ~1.380L)
 * Extraído de App.jsx v29.3 modular (via @babel/traverse)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import * as XLSX from 'xlsx';
import { M, F } from '../../tokens/theme.js';
import { BANCO_CHUVA_UF } from '../../data/banco-chuva-uf.js';
import { KPIS_QUALI_SCHEMA, KPIS_QUANTI_SCHEMA } from '../../data/kpis-schemas.js';
import { PAYLOAD_LOTE1_PR } from '../../data/payload-lote1-pr.js';
import { DADOS } from '../../data/lotes-pr.js';
import { Card1Qualitativo } from './Card1Qualitativo.jsx';
import { Card3KPIs } from './Card3KPIs.jsx';
import { Card4Similaridade } from './Card4Similaridade.jsx';
import { Card5Orcamento } from './Card5Orcamento.jsx';
import { Card6Salvar } from './Card6Salvar.jsx';
import { BotoeiraAcoes } from './BotoeiraAcoes.jsx';
import { TabelaKPIs } from './TabelaKPIs.jsx';
import { RelatoriosPage } from '../relatorios/RelatoriosPage.jsx';
import { S, extKm, sumBy } from '../../globals.js';
import { LinhaTabelaKPI } from './LinhaTabelaKPI.jsx';
import { mesclarPreset } from '../agentes/mesclarPreset.jsx';
import { featuresDoLote } from '../agentes/featuresDoLote.jsx';
import { featuresDoExtra } from '../agentes/featuresDoExtra.jsx';
import { Fonte } from '../shared/ArtefatoHelpers.jsx';
import { API, loadKbExtras, loadOrcGerados } from '../../app-globals.js';

const NovoEstudoFluxoPage = memo(function NovoEstudoFluxoPage_Inner(){
  const [kbExtras] = useState(()=>loadKbExtras());

  // ──────────────────────────────────────────────────────────────────
  // FASE 2 · ESTADO EXPANDIDO + PERSISTÊNCIA (v28.4)
  // 22 KPIs Qualitativos · 18 KPIs Quantitativos · sub-aba ativa
  // ──────────────────────────────────────────────────────────────────

  // Estado qualitativo · 22 campos (lê localStorage no boot)
  const [qual, setQual] = useState(() => {
    let saved = {};
    try {
      const raw = localStorage.getItem('manta-novo-estudo-qual');
      if (raw) saved = JSON.parse(raw) || {};
    } catch(e){}
    return {
      // Bloco A
      nome:'', codManta:'', ufPrincipal:'PR', ufsSec:'', cidade:'', tipoObra:'Concessão Rodoviária Federal',
      // Bloco B
      concessionaria:'', regulador:'ANTT', fiscalSec:'', financiador:'',
      // Bloco C
      ctxRural:0, ctxUrbano:0, ctxSerra:0,
      chuvaMM:1500, chuvaDias:110, chuvaRegime:'Regular',
      altitudeMedia:0, biomaPredominante:'',
      // Bloco D
      prazoAnos:30, dataEstudo:'', nivelDetalhe:'EVTEA', fonteOrcamento:'SICRO', anoBaseSicro:'2024', notas:'',
      // Compatibilidade com Cards 3-6 existentes
      uf:'PR',
      ...saved,
    };
  });

  // Estado quantitativo · 18 KPIs (lê localStorage no boot)
  const [kpis, setKpis] = useState(() => {
    let saved = {};
    try {
      const raw = localStorage.getItem('manta-novo-estudo-kpis');
      if (raw) saved = JSON.parse(raw) || {};
    } catch(e){}
    return {
      // Grupo 1 · Extensão (6)
      extKm:0, extPistaSimples:0, extPistaDupla:0, dupKm:0, fxKm:0, ciclKm:0,
      // Grupo 2 · OAEs e travessias (4)
      oaeQt:0, oaeNovasQt:0, passQt:0, areaEscapeQt:0,
      // Grupo 3 · Interseções e acessos (5)
      interQt:0, retornoXQt:0, margTrechos:0, acessosQt:0, travUrbanasQt:0,
      // Grupo 4 · Outros (3)
      correcoesTracadoQt:0, ilumCurvasQt:0, dispositivosEspQt:0,
      // Legado (Cards 3-6 ainda usam isto)
      margKm:0,
      ...saved,
    };
  });

  // Sub-aba ativa (1=Identif · 2=KPIs · 3=Orçamentação · 4=Exportar · 5=Relatórios)  v28.6
  const [subAba, setSubAba] = useState(() => {
    try { return Number(localStorage.getItem('manta-novo-estudo-subaba')) || 1; }
    catch(e){ return 1; }
  });

  // Sidebar · v28.6 · auto-colapsa quando KPIs ativa (subAba=2) · override manual via toggle
  const [sidebarOverride, setSidebarOverride] = useState(() => {
    try {
      const raw = localStorage.getItem('manta-novo-estudo-sidebar');
      return raw ? raw : 'auto';
    } catch(e){ return 'auto'; }
  });
  // Estado derivado: auto → colapsa se subAba=2 (KPIs); senão expandido
  const sidebarColapsado = sidebarOverride === 'manual-col' || (sidebarOverride === 'auto' && subAba === 3);
  const toggleSidebar = () => {
    setSidebarOverride(prev => {
      const novo = sidebarColapsado ? 'manual-exp' : 'manual-col';
      try { localStorage.setItem('manta-novo-estudo-sidebar', novo); } catch(e){}
      return novo;
    });
  };

  // Rastreamento de origem por campo · v28.5
  // valores: 'manual' | 'preset' | 'import' | 'extraido' | undefined (vazio)
  const [fontes, setFontes] = useState(() => {
    try {
      const raw = localStorage.getItem('manta-novo-estudo-fontes');
      return raw ? JSON.parse(raw) : {};
    } catch(e){ return {}; }
  });

  const [escopo, setEscopo] = useState(null);
  const [pesoFis, setPesoFis] = useState(0.7);
  const [loteEscolhido, setLoteEscolhido] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const [salvosHist, setSalvosHist] = useState(()=>loadOrcGerados());

  // Persistência automática (debounce 300ms)
  useEffect(() => {
    const t = setTimeout(() => {
      try { localStorage.setItem('manta-novo-estudo-qual', JSON.stringify(qual)); } catch(e){}
    }, 300);
    return () => clearTimeout(t);
  }, [qual]);

  useEffect(() => {
    const t = setTimeout(() => {
      try { localStorage.setItem('manta-novo-estudo-kpis', JSON.stringify(kpis)); } catch(e){}
    }, 300);
    return () => clearTimeout(t);
  }, [kpis]);

  useEffect(() => {
    try { localStorage.setItem('manta-novo-estudo-subaba', String(subAba)); } catch(e){}
  }, [subAba]);

  // Persistência das fontes · v28.5
  useEffect(() => {
    const t = setTimeout(() => {
      try { localStorage.setItem('manta-novo-estudo-fontes', JSON.stringify(fontes)); } catch(e){}
    }, 300);
    return () => clearTimeout(t);
  }, [fontes]);

  // Setters · v28.7 · useCallback estabiliza referência (necessário para memo das LinhaTabelaKPI funcionar)
  // setQual/setKpis/setFontes do useState já são estáveis pelo React, então deps = []
  const setQ = useCallback((k, v, fonte = 'manual') => {
    setQual(p => ({...p, [k]: v}));
    setFontes(p => ({...p, [k]: fonte}));
  }, []);
  const setK = useCallback((k, v, fonte = 'manual') => {
    setKpis(p => ({...p, [k]: v}));
    setFontes(p => ({...p, [k]: fonte}));
  }, []);

  // Aplicar preset · v28.7 · 3 setters únicos (eram N+1, causava re-renders em loop)
  const aplicarPreset = (preset) => {
    const novasFontes = {};
    setQual(prevQual => {
      const novoQual = mesclarPreset(prevQual, preset.quali || {});
      Object.keys(preset.quali || {}).forEach(k => {
        const atual = prevQual[k];
        const vazio = atual === undefined || atual === null || atual === '' || atual === 0;
        if (vazio && novoQual[k] !== atual) novasFontes[k] = 'preset';
      });
      return novoQual;
    });
    setKpis(prevKpis => {
      const novoKpis = mesclarPreset(prevKpis, preset.quanti || {});
      Object.keys(preset.quanti || {}).forEach(k => {
        const atual = prevKpis[k];
        const vazio = atual === undefined || atual === null || atual === '' || atual === 0;
        if (vazio && novoKpis[k] !== atual) novasFontes[k] = 'preset';
      });
      return novoKpis;
    });
    if (Object.keys(novasFontes).length > 0) {
      setFontes(prev => ({...prev, ...novasFontes}));
    }
  };

  // Limpar valor + fonte · v28.7 · useCallback para estabilizar referência
  const limparCampoQuali = useCallback((k) => {
    setQual(p => ({...p, [k]: ''}));
    setFontes(p => {
      const novo = {...p};
      delete novo[k];
      return novo;
    });
  }, []);
  const limparCampoQuanti = useCallback((k) => {
    setKpis(p => ({...p, [k]: 0}));
    setFontes(p => {
      const novo = {...p};
      delete novo[k];
      return novo;
    });
  }, []);

  // Iniciar com payload pré-carregado (extração massiva) · v28.5
  const iniciarComPayload = (payload) => {
    if (!payload) return;
    const conf = window.confirm(
      `Carregar "${payload._label}"?\n\n` +
      `Isso vai SUBSTITUIR todos os campos atuais por:\n` +
      `· ${Object.values(payload.qual || {}).filter(v=>v!==''&&v!==0&&v!=null).length}/22 KPIs Qualitativos\n` +
      `· ${Object.values(payload.kpis || {}).filter(v=>v!==0&&v!=null).length}/18 KPIs Quantitativos\n` +
      `· Fontes (manual/preset/import/extraído) atribuídas automaticamente\n\n` +
      `Continuar?`
    );
    if (!conf) return;
    // Aplicar payload · useEffect cuida da persistência (v28.7 · removida race condition)
    setQual(p => ({...p, ...payload.qual, uf: payload.qual?.ufPrincipal || p.uf}));
    setKpis(p => ({...p, ...payload.kpis}));
    setFontes(payload.fontes || {});
  };

  // ─── v28.11 · Estado do agente orquestrador · steps em tempo real ─────
  const [agentSteps, setAgentSteps] = useState([]);  // [{nome, status:'pending'|'running'|'done'|'erro', msg, ts}]
  const [agentRodando, setAgentRodando] = useState(false);
  const [agentResultado, setAgentResultado] = useState(null);

  const addAgentStep = (nome, status = 'running', msg = '') => {
    setAgentSteps(prev => [...prev, {nome, status, msg, ts: Date.now()}]);
  };
  const updateLastStep = (status, msg) => {
    setAgentSteps(prev => {
      if (prev.length === 0) return prev;
      const novo = [...prev];
      novo[novo.length - 1] = {...novo[novo.length - 1], status, msg};
      return novo;
    });
  };
  const resetAgent = () => {
    setAgentSteps([]);
    setAgentRodando(false);
    setAgentResultado(null);
  };

  // ─── v28.11 · Processar arquivo do computador (JSON/XLSX/CSV) ─────────
  const processarArquivoUpload = (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    const reader = new FileReader();

    reader.onerror = () => {
      alert(`❌ Erro ao ler arquivo: ${file.name}`);
    };

    if (ext === 'json') {
      reader.onload = (e) => {
        try {
          const obj = JSON.parse(e.target.result);
          const payload = {
            _label: `Upload · ${file.name}`,
            qual: obj.qual || obj,
            kpis: obj.kpis || {},
            fontes: obj.fontes || {},
          };
          if (!payload.qual || typeof payload.qual !== 'object') {
            alert("❌ JSON inválido · esperado objeto com 'qual' e 'kpis'.");
            return;
          }
          iniciarComPayload(payload);
        } catch(err) {
          alert(`❌ JSON malformado em ${file.name}:\n${err.message}`);
        }
      };
      reader.readAsText(file, 'utf-8');
    } else if (ext === 'xlsx' || ext === 'xls') {
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const wb = XLSX.read(data, {type:'array'});
          // Tentar achar aba com nome conhecido OU usar a primeira
          let sheetName = wb.SheetNames.find(n => /quali|kpis|identif|backup|manta/i.test(n));
          if (!sheetName) sheetName = wb.SheetNames[0];
          const sheet = wb.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(sheet, {header:1, defval:''});
          // Procurar formato "campo / valor" em 2 colunas
          const qual = {}, kpis = {}, fontes = {};
          // Schema de keys conhecidas
          const TODAS_KEYS_QUALI = new Set(Object.values(KPIS_QUALI_SCHEMA).flatMap(b => b.campos.map(c => c.k)));
          const TODAS_KEYS_QUANTI = new Set(Object.values(KPIS_QUANTI_SCHEMA).flatMap(b => b.campos.map(c => c.k)));
          rows.forEach(row => {
            const campo = String(row[0] || '').trim();
            const valor = row[1];
            if (!campo || valor === '' || valor === undefined) return;
            // Match por chave exata OU label
            if (TODAS_KEYS_QUALI.has(campo)) {
              qual[campo] = valor;
              fontes[campo] = 'import';
            } else if (TODAS_KEYS_QUANTI.has(campo)) {
              kpis[campo] = Number(valor) || 0;
              fontes[campo] = 'import';
            } else {
              // Tentar match por label
              for (const bloco of Object.values(KPIS_QUALI_SCHEMA)) {
                const c = bloco.campos.find(c => c.label.toLowerCase() === campo.toLowerCase());
                if (c) { qual[c.k] = valor; fontes[c.k] = 'import'; return; }
              }
              for (const grupo of Object.values(KPIS_QUANTI_SCHEMA)) {
                const c = grupo.campos.find(c => c.label.toLowerCase() === campo.toLowerCase());
                if (c) { kpis[c.k] = Number(valor) || 0; fontes[c.k] = 'import'; return; }
              }
            }
          });
          const totalQuali = Object.keys(qual).length;
          const totalQuanti = Object.keys(kpis).length;
          if (totalQuali === 0 && totalQuanti === 0) {
            alert(
              `❌ Nenhum KPI reconhecido em "${sheetName}".\n\n` +
              `Formato esperado: 2 colunas (campo · valor)\n` +
              `Coluna A: nome do campo (ex: "extKm", "Extensão Total" ou "nome")\n` +
              `Coluna B: valor numérico ou texto\n\n` +
              `Se for um arquivo bruto sem esse formato, use o agente (Opção F).`
            );
            return;
          }
          iniciarComPayload({
            _label: `Upload Excel · ${file.name} (aba "${sheetName}")`,
            qual, kpis, fontes,
          });
        } catch(err) {
          alert(`❌ Erro ao processar Excel ${file.name}:\n${err.message}`);
        }
      };
      reader.readAsArrayBuffer(file);
    } else if (ext === 'csv') {
      reader.onload = (e) => {
        try {
          const txt = e.target.result;
          const linhas = txt.split(/\r?\n/).filter(l => l.trim());
          const qual = {}, kpis = {}, fontes = {};
          const TODAS_KEYS_QUALI = new Set(Object.values(KPIS_QUALI_SCHEMA).flatMap(b => b.campos.map(c => c.k)));
          const TODAS_KEYS_QUANTI = new Set(Object.values(KPIS_QUANTI_SCHEMA).flatMap(b => b.campos.map(c => c.k)));
          linhas.forEach((linha, idx) => {
            if (idx === 0 && /campo|valor|key|value/i.test(linha)) return;
            const partes = linha.split(/[,;]/).map(s => s.trim().replace(/^"|"$/g, ''));
            const campo = partes[0], valor = partes[1];
            if (!campo || !valor) return;
            if (TODAS_KEYS_QUALI.has(campo)) {
              qual[campo] = valor;
              fontes[campo] = 'import';
            } else if (TODAS_KEYS_QUANTI.has(campo)) {
              kpis[campo] = Number(valor) || 0;
              fontes[campo] = 'import';
            }
          });
          const total = Object.keys(qual).length + Object.keys(kpis).length;
          if (total === 0) {
            alert(`❌ Nenhum KPI reconhecido no CSV. Esperado 2 colunas: campo,valor.`);
            return;
          }
          iniciarComPayload({_label: `Upload CSV · ${file.name}`, qual, kpis, fontes});
        } catch(err) {
          alert(`❌ Erro ao processar CSV:\n${err.message}`);
        }
      };
      reader.readAsText(file, 'utf-8');
    } else if (ext === 'pdf') {
      alert(
        `📕 PDF detectado: ${file.name}\n\n` +
        `Parser direto não suporta PDF. Use a OPÇÃO F (🤖 Agente Orquestrador) ` +
        `que envia o PDF pro Claude pra extração semântica.`
      );
    } else {
      alert(
        `❌ Formato ".${ext}" não suportado pelo parser direto.\n\n` +
        `Aceitos: .json, .xlsx, .xls, .csv\n` +
        `Para outros formatos, use a OPÇÃO F · 🤖 Agente Orquestrador.`
      );
    }
  };

  // ─── v28.11 · Agente Orquestrador (Claude API multi-step) ─────────────
  const chamarAgenteOrquestrador = async (entrada) => {
    // entrada: { tipo: 'file'|'json'|'sharepoint'|'preset', dados: ..., contexto: ... }
    resetAgent();
    setAgentRodando(true);

    try {
      // STEP 1 · Detectar tipo
      addAgentStep('1. Identificando entrada', 'running');
      await new Promise(r => setTimeout(r, 400));
      let descricaoEntrada;
      if (entrada.tipo === 'file') {
        descricaoEntrada = `arquivo "${entrada.dados.name}" (${(entrada.dados.size/1024).toFixed(1)} KB · tipo ${entrada.dados.type || 'desconhecido'})`;
      } else if (entrada.tipo === 'json') {
        descricaoEntrada = `JSON colado (${entrada.dados.length} chars)`;
      } else if (entrada.tipo === 'preset') {
        descricaoEntrada = `preset "${entrada.contexto?.nome || 'desconhecido'}"`;
      } else {
        descricaoEntrada = `entrada genérica (${entrada.tipo})`;
      }
      updateLastStep('done', descricaoEntrada);

      // STEP 2 · Ler conteúdo (local · rápido)
      addAgentStep('2. Lendo conteúdo', 'running');
      let conteudoTexto = '';
      let isPdfBase64 = false;
      let pdfBase64Data = null;
      if (entrada.tipo === 'file') {
        const file = entrada.dados;
        const ext = file.name.split('.').pop().toLowerCase();
        if (ext === 'pdf') {
          // PDF → base64 pra mandar pro Claude
          const buf = await file.arrayBuffer();
          const bytes = new Uint8Array(buf);
          let bin = '';
          for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
          pdfBase64Data = btoa(bin);
          isPdfBase64 = true;
          conteudoTexto = `[PDF binário de ${(buf.byteLength/1024).toFixed(1)} KB · enviado como anexo]`;
        } else if (ext === 'json' || ext === 'csv' || ext === 'txt' || ext === 'md') {
          conteudoTexto = await file.text();
        } else if (ext === 'xlsx' || ext === 'xls') {
          const buf = await file.arrayBuffer();
          const wb = XLSX.read(new Uint8Array(buf), {type:'array'});
          // Converter todas as abas em CSV pra mandar pro Claude
          conteudoTexto = wb.SheetNames.map(n => {
            const ws = wb.Sheets[n];
            return `### Aba "${n}":\n${XLSX.utils.sheet_to_csv(ws)}`;
          }).join('\n\n');
        } else {
          throw new Error(`Extensão ".${ext}" não suportada pelo agente.`);
        }
      } else if (entrada.tipo === 'json') {
        conteudoTexto = entrada.dados;
      } else if (entrada.tipo === 'preset') {
        conteudoTexto = JSON.stringify(entrada.dados, null, 2);
      }
      updateLastStep('done', `${conteudoTexto.length} chars lidos${isPdfBase64 ? ' (PDF como anexo)' : ''}`);

      // STEP 3 · Chamar Claude API · classificar estrutura
      addAgentStep('3. Classificando estrutura', 'running');
      const promptClass = `Você é um agente orquestrador da Manta Associados (consultoria de infraestrutura rodoviária).
Analise o seguinte conteúdo e classifique-o em UMA categoria:
- "manta_backup_json" (JSON com qual/kpis/fontes já no formato Manta)
- "per_pdf" (PDF de PER · Programa de Exploração da Rodovia)
- "mef_pdf" (PDF de Modelo Econômico-Financeiro)
- "edital_pdf" (PDF de Edital ANTT/ANEEL)
- "xlsx_kpis_brutos" (Excel com colunas tipo "campo · valor" reconhecíveis)
- "xlsx_kpis_desconhecidos" (Excel com colunas/estrutura desconhecida · precisa inferência)
- "csv_simples" (CSV com 2 colunas campo,valor)
- "outro" (não reconhecido · explicar no campo razão)

Responda APENAS em JSON: {"categoria":"...", "confianca":0-1, "razao":"...", "tem_quali":bool, "tem_quanti":bool}

Conteúdo (primeiros 3000 chars):
\`\`\`
${conteudoTexto.slice(0, 3000)}
\`\`\``;

      const messagesClass = isPdfBase64 ? [
        {role:'user', content:[
          {type:'document', source:{type:'base64', media_type:'application/pdf', data:pdfBase64Data}},
          {type:'text', text: promptClass.replace(/Conteúdo \(primeiros[^]+/, 'O conteúdo é o PDF anexo.')}
        ]}
      ] : [
        {role:'user', content: promptClass}
      ];

      const respClass = await fetch("https://api.anthropic.com/v1/messages", {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 500,
          messages: messagesClass,
        })
      });
      // v28.12 · P01: tratar HTTP error explicitamente
      if (!respClass.ok) {
        const errText = await respClass.text();
        throw new Error(`API retornou ${respClass.status}: ${errText.slice(0,200)}`);
      }
      const dataClass = await respClass.json();
      if (dataClass.error) {
        throw new Error(`API error: ${dataClass.error.message || JSON.stringify(dataClass.error)}`);
      }
      const textClass = (dataClass.content||[]).filter(c=>c.type==='text').map(c=>c.text).join('\n');
      let classificacao;
      try {
        const m = textClass.match(/\{[\s\S]*\}/);
        classificacao = JSON.parse(m ? m[0] : textClass);
      } catch(e) {
        classificacao = {categoria:'outro', confianca:0, razao:'parsing falhou: ' + e.message};
      }
      updateLastStep('done', `${classificacao.categoria} (confiança ${(classificacao.confianca*100).toFixed(0)}%)`);

      // STEP 4 · Extrair KPIs
      addAgentStep('4. Extraindo KPIs estruturados', 'running');

      // Schema esperado · pra ajudar o Claude
      const schemaQuali = Object.values(KPIS_QUALI_SCHEMA).flatMap(b => b.campos.map(c => ({k:c.k, label:c.label, sufixo:c.sufixo||''})));
      const schemaQuanti = Object.values(KPIS_QUANTI_SCHEMA).flatMap(b => b.campos.map(c => ({k:c.k, label:c.label, sufixo:c.sufixo||''})));

      const promptExtr = `Você é o agente extrator da Manta. Categoria detectada: ${classificacao.categoria}.

Extraia do conteúdo TODOS os valores que correspondam aos campos abaixo. Retorne SOMENTE JSON.

CAMPOS QUALITATIVOS (22): ${JSON.stringify(schemaQuali)}
CAMPOS QUANTITATIVOS (18): ${JSON.stringify(schemaQuanti)}

REGRAS:
- "qual": objeto com chaves do schema quali, valores TEXTO (ou número, ou null se ausente)
- "kpis": objeto com chaves do schema quanti, valores NÚMERO (km, un, etc.)
- "fontes": objeto com mesmas chaves preenchidas, valor sempre "extraido"
- Se NÃO encontrar um campo, NÃO inclua (não invente · fail-loud Cap 16)
- Se valor estiver em formato textual ("R$ 6,5 bi"), converter pra número (6500000000)

Formato: {"qual":{...}, "kpis":{...}, "fontes":{...}, "_meta":{"campos_qualis_extraidos":N, "campos_quantis_extraidos":N}}

Conteúdo${isPdfBase64 ? ' está no PDF anexo' : ''}:
${isPdfBase64 ? '' : '```\n' + conteudoTexto.slice(0, 8000) + '\n```'}`;

      const messagesExtr = isPdfBase64 ? [
        {role:'user', content:[
          {type:'document', source:{type:'base64', media_type:'application/pdf', data:pdfBase64Data}},
          {type:'text', text: promptExtr}
        ]}
      ] : [
        {role:'user', content: promptExtr}
      ];

      const respExtr = await fetch("https://api.anthropic.com/v1/messages", {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 4000,
          messages: messagesExtr,
        })
      });
      // v28.12 · P01: tratar HTTP error explicitamente
      if (!respExtr.ok) {
        const errText = await respExtr.text();
        throw new Error(`API extração retornou ${respExtr.status}: ${errText.slice(0,200)}`);
      }
      const dataExtr = await respExtr.json();
      if (dataExtr.error) {
        throw new Error(`API error: ${dataExtr.error.message || JSON.stringify(dataExtr.error)}`);
      }
      const textExtr = (dataExtr.content||[]).filter(c=>c.type==='text').map(c=>c.text).join('\n');
      let extracao;
      try {
        const m = textExtr.match(/\{[\s\S]*\}/);
        extracao = JSON.parse(m ? m[0] : textExtr);
      } catch(e) {
        throw new Error(`Parsing da extração falhou: ${e.message}`);
      }
      const nQuali = Object.keys(extracao.qual || {}).length;
      const nQuanti = Object.keys(extracao.kpis || {}).length;
      updateLastStep('done', `${nQuali} quali · ${nQuanti} quanti extraídos`);

      // STEP 5 · Validação local (sem chamada API)
      addAgentStep('5. Validando estrutura', 'running');
      await new Promise(r => setTimeout(r, 300));
      const TODAS_KEYS_QUALI = new Set(Object.values(KPIS_QUALI_SCHEMA).flatMap(b => b.campos.map(c => c.k)));
      const TODAS_KEYS_QUANTI = new Set(Object.values(KPIS_QUANTI_SCHEMA).flatMap(b => b.campos.map(c => c.k)));
      const qualLimpo = {}, kpisLimpo = {}, fontesLimpo = {};
      Object.entries(extracao.qual || {}).forEach(([k, v]) => {
        if (TODAS_KEYS_QUALI.has(k) && v !== null && v !== '') {
          qualLimpo[k] = v;
          fontesLimpo[k] = 'extraido';
        }
      });
      Object.entries(extracao.kpis || {}).forEach(([k, v]) => {
        if (TODAS_KEYS_QUANTI.has(k) && v !== null && v !== 0 && v !== '') {
          kpisLimpo[k] = Number(v) || 0;
          fontesLimpo[k] = 'extraido';
        }
      });
      const rejeitados = nQuali + nQuanti - Object.keys(qualLimpo).length - Object.keys(kpisLimpo).length;
      updateLastStep('done', `${rejeitados} campos rejeitados (não estão no schema)`);

      // STEP 6 · Aplicar ao portal
      addAgentStep('6. Pronto para aplicar', 'done', `${Object.keys(qualLimpo).length + Object.keys(kpisLimpo).length} campos validados`);

      const payload = {
        _label: `🤖 Agente · ${descricaoEntrada}`,
        qual: qualLimpo,
        kpis: kpisLimpo,
        fontes: fontesLimpo,
      };
      setAgentResultado({payload, classificacao, totalCampos: Object.keys(qualLimpo).length + Object.keys(kpisLimpo).length});

    } catch(err) {
      updateLastStep('erro', err.message || String(err));
      setAgentRodando(false);
    }
  };


  const onChangeUF = (uf) => {
    const c = BANCO_CHUVA_UF[uf] || {mm:1500,dias:110,regime:'Regular'};
    setQual(p => ({...p, uf, ufPrincipal:uf, chuvaMM:c.mm, chuvaDias:c.dias, chuvaRegime:c.regime}));
    setFontes(p => ({...p, ufPrincipal:'manual', chuvaMM:'preset', chuvaDias:'preset', chuvaRegime:'preset'}));
  };

  // Status do único card usado · v28.7
  const card3Completo = kpis.extKm > 0;

  const baseLotes = useMemo(() => {
    const oficiais = ['3','4','5'].map(featuresDoLote);
    const extras = kbExtras.map(featuresDoExtra);
    return [...oficiais, ...extras];
  }, [kbExtras]);

  // Filtros geográficos
  const filtros = useMemo(() => {
    const ufP = qual.uf;
    const regiaoP = UF_REGIAO[ufP] || 'Sul';
    const proximas = (() => {
      const r = regiaoP;
      if (r==='Sul') return ['Sul','Sudeste'];
      if (r==='Sudeste') return ['Sudeste','Sul','Centro-Oeste'];
      if (r==='Centro-Oeste') return ['Centro-Oeste','Sudeste','Norte','Nordeste'];
      if (r==='Nordeste') return ['Nordeste','Centro-Oeste'];
      if (r==='Norte') return ['Norte','Centro-Oeste'];
      return [r];
    })();
    return {
      estado:    baseLotes.filter(l => l.uf === ufP),
      regiao:    baseLotes.filter(l => l.regiao === regiaoP),
      proximos:  baseLotes.filter(l => proximas.includes(l.regiao)),
      todos:     baseLotes,
    };
  }, [baseLotes, qual.uf]);

  const escopoSugerido = useMemo(() => {
    if (filtros.estado.length > 0) return 'estado';
    if (filtros.regiao.length > 0) return 'regiao';
    if (filtros.proximos.length > 0) return 'proximos';
    return 'todos';
  }, [filtros]);

  const escopoAtivo = escopo || escopoSugerido;
  const casosFiltrados = filtros[escopoAtivo] || [];

  const projeto = useMemo(() => {
    const ext = kpis.extKm || 1;
    return {
      densDup:kpis.dupKm/ext, densMarg:kpis.margKm/ext, densCicl:kpis.ciclKm/ext,
      densOae:kpis.oaeQt/ext, densInter:kpis.interQt/ext,
      ctxRural:qual.ctxRural, ctxUrbano:qual.ctxUrbano, ctxSerra:qual.ctxSerra,
      chuvaNorm: (qual.chuvaMM||1500)/2500,
    };
  }, [kpis, qual]);

  const similaridades = useMemo(() => {
    const featNames = ['densDup','densMarg','densCicl','densOae','densInter'];
    const pesoCtx = 1 - pesoFis;
    return casosFiltrados.map(l => {
      const va = featNames.map(f => l[f]||0);
      const vb = featNames.map(f => projeto[f]||0);
      const vca = [l.ctxRural, l.ctxUrbano, l.ctxSerra, (l.chuvaMM||1500)/2500];
      const vcb = [projeto.ctxRural, projeto.ctxUrbano, projeto.ctxSerra, projeto.chuvaNorm];
      const cosFis = cosseno(va, vb);
      const cosCtx = cosseno(vca, vcb);
      const cos = pesoFis*cosFis + pesoCtx*cosCtx;
      // Drivers explicativos
      const drivers = [];
      if (Math.abs(l.densOae - projeto.densOae) < 0.05) drivers.push('densidade OAE similar');
      if (Math.abs(l.densDup - projeto.densDup) < 0.05) drivers.push('% duplicação similar');
      if (l.uf === qual.uf) drivers.push('mesma UF');
      if (l.regiao === (UF_REGIAO[qual.uf]||'Sul')) drivers.push('mesma região');
      if (Math.abs(l.chuvaMM - qual.chuvaMM) < 200) drivers.push('regime de chuva similar');
      return {sig:l.sig, lote:l, cos, cosFis, cosCtx, score: Math.round(cos*100), drivers};
    }).sort((a,b)=> b.cos - a.cos);
  }, [casosFiltrados, projeto, pesoFis, qual.uf, qual.chuvaMM]);

  const loteRef = useMemo(() => {
    if (loteEscolhido) return casosFiltrados.find(l => l.sig === loteEscolhido);
    return similaridades[0]?.lote;
  }, [loteEscolhido, casosFiltrados, similaridades]);

  const orcamento = useMemo(() => {
    if (!loteRef) return null;
    const cats = [
      {k:'dup',l:'Duplicações',q:kpis.dupKm,und:'km'},
      {k:'marg',l:'Marginais',q:kpis.margKm,und:'km'},
      {k:'cicl',l:'Ciclovias',q:kpis.ciclKm,und:'km'},
      {k:'oae',l:'OAEs',q:kpis.oaeQt,und:'un'},
      {k:'pass',l:'Passarelas',q:kpis.passQt,und:'un'},
      {k:'inter',l:'Interseções',q:kpis.interQt,und:'un'},
    ];
    const med = (campo) => baseLotes.reduce((s,l)=>s+(l.pu[campo]||0),0)/Math.max(baseLotes.length,1);
    const rotaA = cats.map(c => ({...c, pu: loteRef.pu[c.k]||0, custo: c.q*(loteRef.pu[c.k]||0)}));
    const rotaB = cats.map(c => ({...c, pu: med(c.k), custo: c.q*med(c.k)}));
    const subA = sumBy(rotaA,'custo');
    const subB = sumBy(rotaB,'custo');
    return {rotaA, rotaB, subA, subB, deltaAB: subB>0?(subA-subB)/subB*100:0,
      med:(subA+subB)/2, min:Math.min(subA,subB), max:Math.max(subA,subB)};
  }, [loteRef, kpis, baseLotes]);

  return (<div style={{padding:'24px 28px', maxWidth:1280, margin:'0 auto', fontFamily:F.body}}>
    {/* Header da página */}
    <div style={{marginBottom:18}}>
      <div style={{fontFamily:F.display, fontSize:22, fontWeight:600, color:M.title, marginBottom:5, paddingBottom:7, borderBottom:`2px solid ${M.borderSoft}`, display:'flex', alignItems:'baseline', gap:14}}>
        <span style={{fontFamily:F.mono, fontSize:11, color:'#fff', fontWeight:700, padding:'4px 11px', background:M.accent, letterSpacing:1, flexShrink:0}}>05</span>
        🎯 Novo Estudo
      </div>
      <div style={{fontSize:11.5, color:M.muted, lineHeight:1.5}}>
        4 etapas · navegação lateral · tabelas únicas com edição inline · coluna Fonte rastreia origem de cada valor (manual / preset / import / extraído).
      </div>
    </div>

    {/* LAYOUT 2 COLUNAS · sidebar (5 sub-abas verticais · colapsável) + área de conteúdo */}
    <div style={{display:'flex', gap:sidebarColapsado ? 12 : 18, alignItems:'flex-start'}}>

      {/* ─── SIDEBAR · MENU VERTICAL · COLAPSÁVEL ─── */}
      <div style={{
        width: sidebarColapsado ? 56 : 210,
        flexShrink:0, position:'sticky', top:16,
        transition:'width .2s ease',
      }}>
        {/* Header do sidebar com toggle */}
        <div style={{
          display:'flex', alignItems:'center', justifyContent: sidebarColapsado ? 'center' : 'space-between',
          marginBottom:6, paddingLeft: sidebarColapsado ? 0 : 4,
        }}>
          {!sidebarColapsado && (<div style={{
            fontFamily:F.mono, fontSize:9.5, color:M.muted, letterSpacing:.6,
            textTransform:'uppercase', fontWeight:700,
          }}>ETAPAS</div>)}
          <button onClick={toggleSidebar} title={sidebarColapsado ? 'Expandir' : 'Colapsar'} style={{
            width:24, height:22, border:`1px solid ${M.border}`, background:'#fff',
            borderRadius:3, cursor:'pointer', fontSize:11, color:M.muted, padding:0,
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
            {sidebarColapsado ? '▶' : '◀'}
          </button>
        </div>

        {[
          {n:1, ic:'🚀', t:'Carregar dados',    sub:'Lote 1 PR / JSON / Manual'},
          {n:2, ic:'🆔', t:'Identificação',     sub:'Nomear e contextualizar'},
          {n:3, ic:'📐', t:'KPIs Quali+Quanti', sub:'22 + 18 campos'},
          {n:4, ic:'⚙️', t:'Orçamentação',      sub:'Similaridade + custo'},
          {n:5, ic:'💾', t:'Exportar',          sub:'Salvar JSON/Excel'},
          {n:6, ic:'📊', t:'Relatórios',        sub:'10 saídas (A1-A5, B1-B5)'},
        ].map(t => {
          const ativo = subAba === t.n;
          return (<div key={t.n} onClick={()=>setSubAba(t.n)} title={sidebarColapsado ? `${t.n}. ${t.t} · ${t.sub}` : ''} style={{
            padding: sidebarColapsado ? '9px 0' : '9px 12px',
            cursor:'pointer', marginBottom:4,
            background: ativo ? '#fff' : 'transparent',
            border: ativo ? `1px solid ${M.border}` : `1px solid transparent`,
            borderLeft: ativo ? `3px solid ${M.accent}` : '3px solid transparent',
            borderRadius:'0 3px 3px 0',
            transition:'all .15s',
            display:'flex', alignItems:'center',
            justifyContent: sidebarColapsado ? 'center' : 'flex-start',
          }}>
            {sidebarColapsado ? (
              <span style={{fontSize:18, opacity: ativo ? 1 : 0.55}}>{t.ic}</span>
            ) : (
              <div style={{display:'flex', alignItems:'baseline', gap:8, width:'100%'}}>
                <span style={{fontSize:15, flexShrink:0}}>{t.ic}</span>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{
                    fontFamily:F.display, fontSize:12, fontWeight:700,
                    color: ativo ? M.title : M.muted, letterSpacing:.3,
                  }}>
                    {t.n}. {t.t}
                  </div>
                  <div style={{fontSize:9.5, color:M.muted, marginTop:1, fontFamily:F.mono}}>
                    {t.sub}
                  </div>
                </div>
              </div>
            )}
          </div>);
        })}
      </div>

      {/* ─── ÁREA DE CONTEÚDO ─── */}
      <div style={{flex:1, minWidth:0}}>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* SUB-ABA 1 · 🚀 CARREGAR DADOS · v28.10 · sempre o ponto de partida */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {subAba === 1 && (<>
          {/* Botão grande NOVO ESTUDO no topo */}
          <div style={{
            marginBottom:18, padding:'16px 18px',
            background:'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
            color:'#fff', borderRadius:6, border:`2px solid ${M.accent}`,
            display:'flex', alignItems:'center', gap:14,
          }}>
            <div style={{fontSize:32, lineHeight:1}}>🆕</div>
            <div style={{flex:1}}>
              <div style={{fontFamily:F.display, fontSize:16, fontWeight:700, color:'#FCD34D', letterSpacing:.4, marginBottom:3}}>
                NOVO ESTUDO
              </div>
              <div style={{fontSize:11.5, color:'#D1D5DB', lineHeight:1.5}}>
                Limpa todos os campos atuais (identificação, 22 KPIs Quali, 18 KPIs Quanti, fontes, orçamento) e prepara o portal para começar do zero. <b style={{color:'#fff'}}>Use sempre que iniciar um novo trabalho</b>.
              </div>
            </div>
            <button onClick={()=>{
              const conf = window.confirm(
                "🆕 INICIAR NOVO ESTUDO\n\n" +
                "Isso vai LIMPAR completamente:\n" +
                "· 22 KPIs Qualitativos (nome, UF, cidade, contexto...)\n" +
                "· 18 KPIs Quantitativos (extensão, OAEs, interseções...)\n" +
                "· Todas as fontes rastreadas\n" +
                "· Histórico de importações\n\n" +
                "⚠️ O estudo atual (se houver) será DESCARTADO.\n\n" +
                "Esta ação não pode ser desfeita. Continuar?"
              );
              if (!conf) return;
              // Limpar tudo
              setQual({nome:'', codManta:'', uf:'', ufPrincipal:'', cidade:'', tipoObra:'',
                concessionaria:'', regulador:'', fiscalSec:'', financiador:'',
                ctxRural:0, ctxUrbano:0, ctxSerra:0,
                chuvaMM:0, chuvaDias:0, chuvaRegime:'', altitudeMedia:0, biomaPredominante:'',
                prazoAnos:0, dataEstudo:'', nivelDetalhe:'', fonteOrcamento:'', anoBaseSicro:'',
                notas:''});
              setKpis({extKm:0, extPistaSimples:0, extPistaDupla:0, dupKm:0, fxKm:0, ciclKm:0,
                oaeQt:0, oaeNovasQt:0, passQt:0, areaEscapeQt:0,
                interQt:0, retornoXQt:0, margTrechos:0, acessosQt:0, travUrbanasQt:0,
                correcoesTracadoQt:0, ilumCurvasQt:0, dispositivosEspQt:0});
              setFontes({});
              setEscopo(null);
              setLoteEscolhido(null);
              setImportStatus(null);
              try {
                localStorage.removeItem('manta-novo-estudo-qual');
                localStorage.removeItem('manta-novo-estudo-kpis');
                localStorage.removeItem('manta-novo-estudo-fontes');
              } catch(e){}
              alert("✓ Tudo limpo! Agora carregue dados pra começar.");
            }} style={{
              padding:'10px 20px', background:M.accent, color:'#fff', border:'none', borderRadius:4,
              fontFamily:F.mono, fontSize:12, fontWeight:700, letterSpacing:.7, cursor:'pointer',
              boxShadow:'0 2px 8px rgba(245,158,11,.4)',
            }}>🆕 LIMPAR TUDO</button>
          </div>

          {/* Instruções */}
          <div style={{
            marginBottom:14, padding:'10px 14px', background:M.bgAlt,
            border:`1px solid ${M.borderSoft}`, borderRadius:4,
            fontSize:11.5, color:M.muted, lineHeight:1.5,
          }}>
            <b style={{color:M.title}}>Etapa 1 · Carregar dados.</b> Escolha uma das 3 opções abaixo para popular os 40 campos (22 quali + 18 quanti). Depois você vai pra <b>Identificação</b> pra nomear · <b>KPIs</b> pra revisar · <b>Orçamentação</b> pra calcular.
          </div>

          {/* 3 cards de carga */}
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:14}}>
            {/* OPÇÃO A · Lote 1 PR pronto */}
            <div style={{
              padding:'14px 16px', background:'#fff',
              border:`2px solid #10B981`, borderRadius:6,
              display:'flex', flexDirection:'column', gap:8,
            }}>
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <span style={{fontSize:22}}>🚀</span>
                <div>
                  <div style={{fontFamily:F.display, fontSize:13, fontWeight:700, color:M.title}}>
                    A · Lote 1 PR (pronto)
                  </div>
                  <div style={{fontFamily:F.mono, fontSize:9.5, color:'#065F46', fontWeight:600, letterSpacing:.3, marginTop:1}}>
                    EXTRAÍDO DO PER · 40 CAMPOS
                  </div>
                </div>
              </div>
              <div style={{fontSize:11, color:M.muted, lineHeight:1.45, flex:1}}>
                Carrega o caso real do Paraná: 473,01 km · 343,81 km duplicação · 98 interseções · 70 OAEs · MEF CAPEX R$ 6,5 bi. Fontes marcadas como <b>extraído</b> (verde).
              </div>
              <button onClick={()=>iniciarComPayload(PAYLOAD_LOTE1_PR)} style={{
                padding:'9px 14px', background:'#10B981', color:'#fff', border:'none', borderRadius:3,
                fontFamily:F.mono, fontSize:11, fontWeight:700, letterSpacing:.5, cursor:'pointer',
              }}>
                🚀 CARREGAR LOTE 1 PR
              </button>
            </div>

            {/* OPÇÃO B · Upload de arquivo · v28.11 */}
            <div
              onDragOver={(e)=>{ e.preventDefault(); e.stopPropagation(); e.currentTarget.style.background='#FEF3C7'; }}
              onDragLeave={(e)=>{ e.preventDefault(); e.stopPropagation(); e.currentTarget.style.background='#fff'; }}
              onDrop={(e)=>{
                e.preventDefault(); e.stopPropagation();
                e.currentTarget.style.background='#fff';
                const file = e.dataTransfer.files[0];
                if (file) processarArquivoUpload(file);
              }}
              style={{
                padding:'14px 16px', background:'#fff',
                border:`2px solid #F59E0B`, borderRadius:6,
                display:'flex', flexDirection:'column', gap:8,
                transition:'background .2s',
              }}>
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <span style={{fontSize:22}}>📁</span>
                <div>
                  <div style={{fontFamily:F.display, fontSize:13, fontWeight:700, color:M.title}}>
                    B · Upload de arquivo
                  </div>
                  <div style={{fontFamily:F.mono, fontSize:9.5, color:'#92400E', fontWeight:600, letterSpacing:.3, marginTop:1}}>
                    .JSON / .XLSX / .CSV
                  </div>
                </div>
              </div>
              <div style={{fontSize:11, color:M.muted, lineHeight:1.45, flex:1}}>
                Carrega arquivo do <b>seu computador</b> · aceita JSON (backup A5), Excel (com colunas 'campo · valor') ou CSV. <b>Arraste e solte aqui</b> ou clique no botão.
              </div>
              <input
                type="file"
                id="aluci-file-upload"
                accept=".json,.xlsx,.xls,.csv,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                style={{display:'none'}}
                onChange={(e)=>{
                  const file = e.target.files[0];
                  if (file) processarArquivoUpload(file);
                  e.target.value = '';
                }}
              />
              <button onClick={()=>document.getElementById('aluci-file-upload').click()} style={{
                padding:'9px 14px', background:'#F59E0B', color:'#fff', border:'none', borderRadius:3,
                fontFamily:F.mono, fontSize:11, fontWeight:700, letterSpacing:.5, cursor:'pointer',
              }}>
                📁 ESCOLHER ARQUIVO
              </button>
            </div>

            {/* OPÇÃO C · Colar JSON */}
            <div style={{
              padding:'14px 16px', background:'#fff',
              border:`2px solid #3B82F6`, borderRadius:6,
              display:'flex', flexDirection:'column', gap:8,
            }}>
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <span style={{fontSize:22}}>📄</span>
                <div>
                  <div style={{fontFamily:F.display, fontSize:13, fontWeight:700, color:M.title}}>
                    C · Colar JSON
                  </div>
                  <div style={{fontFamily:F.mono, fontSize:9.5, color:'#1E40AF', fontWeight:600, letterSpacing:.3, marginTop:1}}>
                    RE-IMPORTAR BACKUP A5
                  </div>
                </div>
              </div>
              <div style={{fontSize:11, color:M.muted, lineHeight:1.45, flex:1}}>
                Cole o JSON gerado por um <b>A5 · Backup re-importável</b> anterior · qualquer estudo salvo · output do <b>aluci-extractor</b> · ou JSON do SharePoint da Manta.
              </div>
              <button onClick={()=>{
                const txt = window.prompt(
                  "Cole o JSON completo do backup A5 abaixo:\n\n" +
                  "Estrutura esperada: { qual:{...}, kpis:{...}, fontes:{...} }"
                );
                if (!txt) return;
                try {
                  const obj = JSON.parse(txt);
                  const payload = {
                    _label: obj._meta?.titulo || obj._label || 'JSON colado',
                    qual: obj.qual || obj,
                    kpis: obj.kpis || {},
                    fontes: obj.fontes || {},
                  };
                  if (!payload.qual || typeof payload.qual !== 'object') {
                    alert("❌ JSON inválido · esperado objeto com 'qual' e 'kpis'.");
                    return;
                  }
                  iniciarComPayload(payload);
                } catch(e) {
                  alert(`❌ JSON malformado:\n${e.message}`);
                }
              }} style={{
                padding:'9px 14px', background:'#3B82F6', color:'#fff', border:'none', borderRadius:3,
                fontFamily:F.mono, fontSize:11, fontWeight:700, letterSpacing:.5, cursor:'pointer',
              }}>
                📄 COLAR JSON
              </button>
            </div>

            {/* OPÇÃO D · SharePoint · v28.11 · refinada v28.12 */}
            <div style={{
              padding:'14px 16px', background:'#fff',
              border:`2px solid #8B5CF6`, borderRadius:6,
              display:'flex', flexDirection:'column', gap:8,
            }}>
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <span style={{fontSize:22}}>☁️</span>
                <div style={{flex:1}}>
                  <div style={{fontFamily:F.display, fontSize:13, fontWeight:700, color:M.title}}>
                    D · SharePoint
                  </div>
                  <div style={{fontFamily:F.mono, fontSize:9.5, color:'#5B21B6', fontWeight:600, letterSpacing:.3, marginTop:1}}>
                    M365 MANTA · 3 PASSOS
                  </div>
                </div>
                <span style={{padding:'1px 6px', background:'#FECACA', color:'#991B1B',
                  fontFamily:F.mono, fontSize:8.5, fontWeight:700, letterSpacing:.4, borderRadius:2}}>
                  SEM FETCH AUTO
                </span>
              </div>
              <div style={{fontSize:10.5, color:M.muted, lineHeight:1.45, flex:1}}>
                <b style={{color:M.title}}>1.</b> Clique abaixo · abre SharePoint Manta em nova aba<br/>
                <b style={{color:M.title}}>2.</b> Localize o arquivo · baixe para o computador<br/>
                <b style={{color:M.title}}>3.</b> Volte aqui · use <b>Opção B</b> (Upload) ou <b>F</b> (Agente)<br/>
                <span style={{color:'#991B1B', fontStyle:'italic', fontSize:9.5}}>Fetch automático requer Backend Manta (P05 · PK_07 · 02.2)</span>
              </div>
              <button onClick={()=>{
                window.open('https://mantaassociados.sharepoint.com/sites/Manta/Documentos%20Compartilhados/Forms/AllItems.aspx', '_blank');
              }} style={{
                padding:'9px 14px', background:'#8B5CF6', color:'#fff', border:'none', borderRadius:3,
                fontFamily:F.mono, fontSize:11, fontWeight:700, letterSpacing:.5, cursor:'pointer',
              }}>
                ☁️ ABRIR SHAREPOINT
              </button>
            </div>

            {/* OPÇÃO E · Manual em branco */}
            <div style={{
              padding:'14px 16px', background:'#fff',
              border:`2px solid ${M.border}`, borderRadius:6,
              display:'flex', flexDirection:'column', gap:8,
            }}>
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <span style={{fontSize:22}}>✍️</span>
                <div>
                  <div style={{fontFamily:F.display, fontSize:13, fontWeight:700, color:M.title}}>
                    E · Manual em branco
                  </div>
                  <div style={{fontFamily:F.mono, fontSize:9.5, color:M.muted, fontWeight:600, letterSpacing:.3, marginTop:1}}>
                    SEM FONTE PRIMÁRIA
                  </div>
                </div>
              </div>
              <div style={{fontSize:11, color:M.muted, lineHeight:1.45, flex:1}}>
                Pula esta etapa e começa direto na Identificação. Todos os 40 campos ficam vazios · você preenche manualmente · fontes marcadas como <b>manual</b> (cinza).
              </div>
              <button onClick={()=>setSubAba(2)} style={{
                padding:'9px 14px', background:'#fff', color:M.title,
                border:`1px solid ${M.border}`, borderRadius:3,
                fontFamily:F.mono, fontSize:11, fontWeight:700, letterSpacing:.5, cursor:'pointer',
              }}>
                ✍️ COMEÇAR MANUAL →
              </button>
            </div>

            {/* OPÇÃO F · 🤖 Agente Orquestrador · v28.11 */}
            <div style={{
              padding:'14px 16px',
              background:'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
              border:`2px solid #059669`, borderRadius:6,
              display:'flex', flexDirection:'column', gap:8,
              gridColumn: 'span 2',
            }}>
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <span style={{fontSize:22}}>🤖</span>
                <div style={{flex:1}}>
                  <div style={{fontFamily:F.display, fontSize:13, fontWeight:700, color:'#065F46'}}>
                    F · Agente Orquestrador
                  </div>
                  <div style={{fontFamily:F.mono, fontSize:9.5, color:'#065F46', fontWeight:600, letterSpacing:.3, marginTop:1}}>
                    CLAUDE SONNET 4 · PIPELINE MULTI-STEP · PDF/XLSX/JSON/QUALQUER
                  </div>
                </div>
                <span style={{padding:'2px 8px', background:'#059669', color:'#fff',
                  fontFamily:F.mono, fontSize:9, fontWeight:700, letterSpacing:.5, borderRadius:3}}>NOVO</span>
              </div>
              <div style={{fontSize:11, color:'#065F46', lineHeight:1.45, flex:1}}>
                Envia o arquivo para o <b>agente Manta</b> (Claude IA) que faz: <b>1.</b> classifica tipo (PER/MEF/edital/XLSX) · <b>2.</b> extrai 22 quali + 18 quanti via inferência semântica · <b>3.</b> valida contra schema · <b>4.</b> aplica fontes corretas. Use para arquivos desconhecidos, PDFs do PER, XLSX brutos do cliente.
              </div>
              <input
                type="file"
                id="aluci-agent-upload"
                accept=".json,.xlsx,.xls,.csv,.pdf,.txt,.md"
                style={{display:'none'}}
                onChange={(e)=>{
                  const file = e.target.files[0];
                  if (file) chamarAgenteOrquestrador({tipo:'file', dados:file});
                  e.target.value = '';
                }}
              />
              <div style={{display:'flex', gap:6}}>
                <button onClick={()=>document.getElementById('aluci-agent-upload').click()}
                  disabled={agentRodando} style={{
                  flex:1, padding:'10px 14px',
                  background: agentRodando ? '#A7F3D0' : '#059669',
                  color:'#fff', border:'none', borderRadius:3,
                  fontFamily:F.mono, fontSize:11, fontWeight:700, letterSpacing:.5,
                  cursor: agentRodando ? 'not-allowed' : 'pointer',
                }}>
                  {agentRodando ? '🤖 PROCESSANDO...' : '🤖 ENVIAR PRO AGENTE'}
                </button>
                <button onClick={()=>{
                  const txt = window.prompt('Cole texto/JSON pro agente analisar:');
                  if (txt) chamarAgenteOrquestrador({tipo:'json', dados:txt});
                }} disabled={agentRodando} title="Colar texto direto" style={{
                  padding:'10px 12px', background:'#fff', color:'#065F46',
                  border:`1px solid #059669`, borderRadius:3,
                  fontFamily:F.mono, fontSize:11, fontWeight:700, letterSpacing:.5,
                  cursor: agentRodando ? 'not-allowed' : 'pointer',
                }}>📋 TEXTO</button>
              </div>
            </div>
          </div>

          {/* ─── Modal de progresso do agente · v28.11 · steps em tempo real ─── */}
          {(agentSteps.length > 0 || agentResultado) && (<div style={{
            marginTop:16, padding:'14px 16px',
            background:'#fff', border:`2px solid #059669`, borderRadius:6,
          }}>
            <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:12}}>
              <span style={{fontSize:18}}>🤖</span>
              <div style={{flex:1, fontFamily:F.display, fontSize:13, fontWeight:700, color:'#065F46'}}>
                Pipeline do Agente Orquestrador
              </div>
              {agentRodando && (<div style={{
                fontFamily:F.mono, fontSize:10, color:'#059669', fontWeight:700,
                animation:'pulse 1.5s infinite',
              }}>● PROCESSANDO</div>)}
              {!agentRodando && (<button onClick={resetAgent} style={{
                padding:'4px 10px', background:'transparent', color:M.muted,
                border:`1px solid ${M.border}`, borderRadius:3,
                fontFamily:F.mono, fontSize:10, fontWeight:600, cursor:'pointer',
              }}>✕ LIMPAR</button>)}
            </div>

            {/* Lista de steps */}
            <div style={{display:'flex', flexDirection:'column', gap:6}}>
              {agentSteps.map((step, idx) => {
                const ico = step.status === 'done' ? '✅' : step.status === 'running' ? '⏳' : step.status === 'erro' ? '❌' : '⚪';
                const cor = step.status === 'done' ? '#065F46' : step.status === 'running' ? '#92400E' : step.status === 'erro' ? '#991B1B' : '#6B7280';
                const bg = step.status === 'done' ? '#D1FAE5' : step.status === 'running' ? '#FEF3C7' : step.status === 'erro' ? '#FECACA' : '#F9FAFB';
                return (<div key={idx} style={{
                  padding:'7px 12px', background:bg, borderRadius:3,
                  display:'flex', alignItems:'center', gap:10,
                }}>
                  <span style={{fontSize:14}}>{ico}</span>
                  <div style={{flex:1, fontSize:11.5, fontWeight:600, color:cor}}>
                    {step.nome}
                  </div>
                  {step.msg && (<div style={{fontSize:10.5, color:cor, fontFamily:F.mono, opacity:.85}}>
                    {step.msg}
                  </div>)}
                </div>);
              })}
            </div>

            {/* Resultado final · botão APLICAR */}
            {agentResultado && (<div style={{
              marginTop:12, padding:'12px 14px',
              background:'#1F2937', color:'#fff', borderRadius:4,
              display:'flex', alignItems:'center', gap:12,
            }}>
              <span style={{fontSize:22}}>🎯</span>
              <div style={{flex:1}}>
                <div style={{fontFamily:F.display, fontSize:13, fontWeight:700, color:'#FCD34D'}}>
                  Extração completa
                </div>
                <div style={{fontSize:11, color:'#D1D5DB', marginTop:2}}>
                  Categoria: <b>{agentResultado.classificacao.categoria}</b> · {agentResultado.totalCampos} campos extraídos · confiança {(agentResultado.classificacao.confianca*100).toFixed(0)}%
                </div>
              </div>
              <button onClick={()=>{
                iniciarComPayload(agentResultado.payload);
                resetAgent();
              }} style={{
                padding:'9px 16px', background:'#059669', color:'#fff', border:'none', borderRadius:3,
                fontFamily:F.mono, fontSize:11, fontWeight:700, letterSpacing:.5, cursor:'pointer',
              }}>
                ✓ APLICAR
              </button>
              <button onClick={resetAgent} style={{
                padding:'9px 14px', background:'transparent', color:'#fff',
                border:`1px solid rgba(255,255,255,.3)`, borderRadius:3,
                fontFamily:F.mono, fontSize:11, fontWeight:600, letterSpacing:.5, cursor:'pointer',
              }}>
                ✕ DESCARTAR
              </button>
            </div>)}
          </div>)}

          {/* Status atual + indicador de progresso */}
          {(Object.keys(fontes).length > 0 || qual.nome) && (<div style={{
            marginTop:16, padding:'10px 14px', background:'#FEF3C7',
            border:`1px solid #F59E0B`, borderRadius:4,
            display:'flex', alignItems:'center', gap:10,
          }}>
            <span style={{fontSize:18}}>⚠️</span>
            <div style={{flex:1, fontSize:11, color:'#92400E', lineHeight:1.5}}>
              <b>Já existe estudo carregado.</b>
              {qual.nome && (<> Nome: <b>{qual.nome.slice(0, 50)}</b>.</>)}
              {' '}{Object.keys(fontes).length} campos preenchidos.
              {' '}Carregar nova fonte vai sobrescrever · use "🆕 LIMPAR TUDO" no topo se quiser começar do zero.
            </div>
            <button onClick={()=>setSubAba(2)} style={{
              padding:'7px 12px', background:M.accent, color:'#fff', border:'none', borderRadius:3,
              fontFamily:F.mono, fontSize:10.5, fontWeight:700, letterSpacing:.5, cursor:'pointer',
            }}>CONTINUAR →</button>
          </div>)}
        </>)}

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* SUB-ABA 2 · IDENTIFICAÇÃO (Card 1 · campos básicos)          */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {subAba === 2 && (<>
          <div style={{
            marginBottom:14, padding:'10px 14px', background:M.bgAlt,
            border:`1px solid ${M.borderSoft}`, borderRadius:4,
            fontSize:11.5, color:M.muted, lineHeight:1.5,
          }}>
            <b style={{color:M.title}}>Etapa 2 · Identificação.</b> Agora que os dados foram carregados, dê um nome ao estudo e revise informações contextuais (UF, cidade, regime de chuvas). Depois vá para <b>KPIs</b> revisar os 40 campos.
          </div>
          <Card1Qualitativo qual={qual} setQ={(k,v)=>setQ(k,v)} onChangeUF={onChangeUF}/>
          <div style={{marginTop:14, display:'flex', justifyContent:'space-between'}}>
            <button onClick={()=>setSubAba(1)} style={{
              padding:'9px 14px', background:'transparent', color:M.muted,
              border:`1px solid ${M.border}`, borderRadius:3,
              fontFamily:F.mono, fontSize:11, fontWeight:600, letterSpacing:.5, cursor:'pointer',
            }}>← CARREGAR DADOS</button>
            <button onClick={()=>setSubAba(3)} style={{
              padding:'9px 18px', background:M.accent, color:'#fff', border:'none', borderRadius:3,
              fontFamily:F.mono, fontSize:11, fontWeight:700, letterSpacing:.5, cursor:'pointer',
            }}>
              IR PARA KPIs →
            </button>
          </div>
        </>)}

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* SUB-ABA 2 · KPIs QUALI+QUANTI (2 tabelas estilo Excel)        */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {subAba === 3 && (<>
          {/* Botoeira de ações */}
          <BotoeiraAcoes
            onAplicarPreset={aplicarPreset}
            onIniciarPayload={iniciarComPayload}
            onAbrirImport={(tipo)=>{
              alert(`Importação ${tipo.toUpperCase()} será habilitada via Backend Manta (PK_07 · 02.2).\n\nPor ora, use:\n· Preset (mescla dados típicos)\n· Botão verde "INICIAR LOTE 1 PR" no topo (extração massiva)\n· Edição manual célula por célula`);
            }}
          />

          {/* Tabela KPIs Qualitativos */}
          <TabelaKPIs
            titulo="KPIs Qualitativos · 22 campos em 4 blocos (A · Identificação · B · Stakeholders · C · Geo/clima · D · Contratual)"
            schema={KPIS_QUALI_SCHEMA}
            valores={qual}
            fontes={fontes}
            onChange={setQ}
            onLimpar={limparCampoQuali}
          />

          {/* Tabela KPIs Quantitativos */}
          <TabelaKPIs
            titulo="KPIs Quantitativos · 18 campos em 4 grupos (1 · Extensão · 2 · OAEs · 3 · Interseções · 4 · Outros)"
            schema={KPIS_QUANTI_SCHEMA}
            valores={kpis}
            fontes={fontes}
            onChange={setK}
            onLimpar={limparCampoQuanti}
          />

          {/* Navegação */}
          <div style={{marginTop:14, display:'flex', justifyContent:'space-between'}}>
            <button onClick={()=>setSubAba(2)} style={{
              padding:'9px 14px', background:'transparent', color:M.muted,
              border:`1px solid ${M.border}`, borderRadius:3,
              fontFamily:F.mono, fontSize:11, fontWeight:600, letterSpacing:.5, cursor:'pointer',
            }}>← IDENTIFICAÇÃO</button>
            <button onClick={()=>setSubAba(4)} style={{
              padding:'9px 18px', background:M.accent, color:'#fff', border:'none', borderRadius:3,
              fontFamily:F.mono, fontSize:11, fontWeight:700, letterSpacing:.5, cursor:'pointer',
            }}>IR PARA ORÇAMENTAÇÃO →</button>
          </div>
        </>)}

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* SUB-ABA 3 · ORÇAMENTAÇÃO (Cards 3-5 legados)                  */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {subAba === 4 && (<>
          <div style={{
            marginBottom:14, padding:'10px 14px', background:M.bgAlt,
            border:`1px solid ${M.borderSoft}`, borderRadius:4,
            fontSize:11.5, color:M.muted, lineHeight:1.5,
          }}>
            <b style={{color:M.title}}>Etapa 4 · Orçamentação.</b> KPIs consolidados → busca de similaridade no portfólio histórico → estimativa de orçamento por categoria.
          </div>

          {/* KPIs Físicos Consolidados */}
          <div style={{marginBottom:18, border:`1px solid ${M.border}`, borderRadius:4, overflow:'hidden', background:'#fff'}}>
            <div style={{padding:'8px 12px', background:M.title, color:'#fff', borderBottom:`2px solid ${M.accent}`, fontFamily:F.display, fontWeight:600, fontSize:13, letterSpacing:.4}}>
              📊 KPIs Físicos Consolidados
            </div>
            <div style={{padding:'12px 14px'}}>
              <Card3KPIs kpis={kpis} setK={setK}/>
            </div>
          </div>

          {/* Buscar Similaridade */}
          <div style={{marginBottom:18, border:`1px solid ${M.border}`, borderRadius:4, overflow:'hidden', background:'#fff'}}>
            <div style={{padding:'8px 12px', background:M.title, color:'#fff', borderBottom:`2px solid ${M.accent}`, fontFamily:F.display, fontWeight:600, fontSize:13, letterSpacing:.4}}>
              🎯 Buscar Similaridade · Top 5
            </div>
            <div style={{padding:'12px 14px'}}>
              {!card3Completo ? (
                <div style={{padding:18, textAlign:'center', color:M.muted, fontSize:11.5, fontStyle:'italic'}}>
                  Preencha os KPIs físicos (acima) antes de buscar similaridade.
                </div>
              ) : (
                <Card4Similaridade
                  qual={qual} kpis={kpis} filtros={filtros}
                  escopoSugerido={escopoSugerido} escopo={escopoAtivo} setEscopo={setEscopo}
                  pesoFis={pesoFis} setPesoFis={setPesoFis}
                  similaridades={similaridades} casosFiltrados={casosFiltrados}
                  loteEscolhido={loteEscolhido} setLoteEscolhido={setLoteEscolhido}
                />
              )}
            </div>
          </div>

          {/* Orçamento Estimado */}
          <div style={{marginBottom:18, border:`1px solid ${M.border}`, borderRadius:4, overflow:'hidden', background:'#fff'}}>
            <div style={{padding:'8px 12px', background:M.title, color:'#fff', borderBottom:`2px solid ${M.accent}`, fontFamily:F.display, fontWeight:600, fontSize:13, letterSpacing:.4}}>
              💰 Orçamento Estimado
            </div>
            <div style={{padding:'12px 14px'}}>
              {!loteRef ? (
                <div style={{padding:18, textAlign:'center', color:M.muted, fontSize:11.5, fontStyle:'italic'}}>
                  Escolha um caso de referência (acima) antes de gerar orçamento.
                </div>
              ) : (
                <Card5Orcamento orcamento={orcamento} loteRef={loteRef} kpis={kpis}/>
              )}
            </div>
          </div>

          {/* Navegação */}
          <div style={{marginTop:14, display:'flex', justifyContent:'space-between'}}>
            <button onClick={()=>setSubAba(3)} style={{
              padding:'9px 14px', background:'transparent', color:M.muted,
              border:`1px solid ${M.border}`, borderRadius:3,
              fontFamily:F.mono, fontSize:11, fontWeight:600, letterSpacing:.5, cursor:'pointer',
            }}>← VOLTAR</button>
            <button onClick={()=>setSubAba(5)} disabled={!orcamento} style={{
              padding:'9px 18px',
              background: orcamento ? M.accent : '#E5E7EB',
              color: orcamento ? '#fff' : '#9CA3AF',
              border:'none', borderRadius:3,
              fontFamily:F.mono, fontSize:11, fontWeight:700, letterSpacing:.5,
              cursor: orcamento ? 'pointer' : 'not-allowed',
            }}>IR PARA EXPORTAR →</button>
          </div>
        </>)}

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* SUB-ABA 4 · EXPORTAR                                          */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {subAba === 5 && (<>
          <div style={{
            marginBottom:14, padding:'10px 14px', background:M.bgAlt,
            border:`1px solid ${M.borderSoft}`, borderRadius:4,
            fontSize:11.5, color:M.muted, lineHeight:1.5,
          }}>
            <b style={{color:M.title}}>Etapa 5 · Exportar.</b> Salve o estudo em JSON/Excel, atribua tags e adicione ao portfólio histórico.
          </div>

          <div style={{marginBottom:18, border:`1px solid ${M.border}`, borderRadius:4, overflow:'hidden', background:'#fff'}}>
            <div style={{padding:'8px 12px', background:M.title, color:'#fff', borderBottom:`2px solid ${M.accent}`, fontFamily:F.display, fontWeight:600, fontSize:13, letterSpacing:.4}}>
              💾 Exportar / Salvar Estudo
            </div>
            <div style={{padding:'12px 14px'}}>
              {!orcamento ? (
                <div style={{padding:18, textAlign:'center', color:M.muted, fontSize:11.5, fontStyle:'italic'}}>
                  Aguardando orçamento (etapa anterior).
                </div>
              ) : (
                <Card6Salvar
                  qual={qual} kpis={kpis} loteRef={loteRef} similaridades={similaridades}
                  orcamento={orcamento} escopo={escopoAtivo} casosFiltrados={casosFiltrados}
                  salvosHist={salvosHist} setSalvosHist={setSalvosHist}
                />
              )}
            </div>
          </div>

          <div style={{marginTop:14, textAlign:'left'}}>
            <button onClick={()=>setSubAba(4)} style={{
              padding:'9px 14px', background:'transparent', color:M.muted,
              border:`1px solid ${M.border}`, borderRadius:3,
              fontFamily:F.mono, fontSize:11, fontWeight:600, letterSpacing:.5, cursor:'pointer',
            }}>← VOLTAR</button>
          </div>
        </>)}

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* SUB-ABA 5 · RELATÓRIOS (10 saídas A1-A5 + B1-B5)              */}
        {/* ═══════════════════════════════════════════════════════════ */}
        {subAba === 6 && (<RelatoriosPage
          qual={qual} kpis={kpis} fontes={fontes}
          orcamento={orcamento} loteRef={loteRef}
          similaridades={similaridades} casosFiltrados={casosFiltrados}
          salvosHist={salvosHist}
          baseLotes={baseLotes}
        />)}

      </div>
    </div>
  </div>);
});

export { NovoEstudoFluxoPage };
export default NovoEstudoFluxoPage;
